import React, { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';

import { getScanningAlgorithm } from 'reducers/cafaConfigs';
import { getAllowFractionalProductQuantities } from 'reducers/configs/settings';
import { getTempAmount } from 'reducers/ShoppingCart';
import {
  removeProduct,
  setTempAmount,
  updateOrderAmount,
} from 'actions/ShoppingCart';
import { handleScanning } from 'actions/scanner';
import { Order } from 'types/ShoppingCart';

import {
  connectLastCharsInRange,
  getAutoFocusInputValue,
  getDisableOrderQty,
  getOrderAmount,
  isScannerInput,
  sanitizeValue,
} from '../utils';

const ScanningRowQuantity = React.memo<Pick<Order, 'orderIndex'>>(
  ({ orderIndex }) => {
    const dispatch = useDispatch();
    const { minScanLength, timeBeforeScanTest } = useSelector(
      getScanningAlgorithm,
    );
    const orderAmount = useSelector(getOrderAmount(orderIndex));

    const autoFocus = useSelector(getAutoFocusInputValue(orderIndex));
    const disabled = useSelector(getDisableOrderQty(orderIndex));
    const tempAmount = useSelector(getTempAmount);
    const isFractionalQuantityAllowed = useSelector(
      getAllowFractionalProductQuantities,
    );
    const amount =
      tempAmount.orderIndex === orderIndex ? tempAmount.amount : orderAmount;


    const [previousValue, setPreviousValue] = useState(orderAmount);
    const [isEmpty, setIsEmpty] = useState(false);
    const [ts, setTs] = useState<number>(0);
    const [scanValueMap, setScanValueMap] = useState<[number, string][]>([]);
    const ref = useRef<HTMLInputElement>(null);

    const handleBlur = () => {
      const [lastScanTS = 0] = scanValueMap[0] ?? [];

      setIsEmpty(false);
      setTempAmount(false, false);
      if (
        String(tempAmount.amount).trim().length &&
        ts &&
        lastScanTS < ts &&
        lastScanTS !== 0
      ) {
        dispatch(updateOrderAmount(tempAmount.amount, orderIndex));
        return;
      }

      if (tempAmount.orderIndex !== orderIndex) {
        return; // Did not edit this row
      }
      let { amount } = tempAmount;
      if (amount === '') {
        return; // Did not enter a non-empty amount
      }
      if (Number.isNaN(Number(amount))) {
        return; // Did not enter a legal amount
      }

      // Apply rounding if configured to only accept integers
      if (!isFractionalQuantityAllowed) {
        const roundedAmount = Math.round(amount);
        // minimum amount after rounding is 1/-1
        amount =
          roundedAmount === 0 && amount !== 0
            ? Math.sign(amount)
            : roundedAmount;
      }

      // The amount is valid, commit it
      if (Number(amount) === 0) {
        dispatch(removeProduct(orderIndex));
      } else {
        dispatch(updateOrderAmount(amount, orderIndex));
      }

      setTs(0);
      setScanValueMap([]);
    };

    useDebounce(
      () => {
        if (!scanValueMap.length) return;

        const [, scanVal] = connectLastCharsInRange(
          scanValueMap,
          timeBeforeScanTest,
        );
        if (scanVal.length >= minScanLength) {
          dispatch(setTempAmount(previousValue, orderIndex));
          dispatch(handleScanning(scanVal));
          ref.current?.blur();
        } else if (scanVal.length === 1) {
          setScanValueMap([]);
        } else {
          dispatch(setTempAmount(previousValue, orderIndex));
          ref.current?.blur();
        }
      },
      timeBeforeScanTest,
      [scanValueMap.length, ref.current],
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value },
        timeStamp,
      } = e;
      const [, scanVal] = connectLastCharsInRange(
        scanValueMap,
        timeBeforeScanTest,
      );

      // if we are not scanning (scanValue up to 1 char), we update the tempAmount state
      if (scanVal.length <= 1) {
        const qty = sanitizeValue(value);
        dispatch(setTempAmount(qty, orderIndex));
        if (Number.isNaN(Number(qty)) || !qty) {
          setIsEmpty(true);
        } else {
          setIsEmpty(false);
          setPreviousValue(orderAmount);
        }
      }
      // we update the state of the timeStamp  regardless if it was a scan or user input to properly calculate the next input interval
      setTs(timeStamp);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      const { key, timeStamp } = e;

      if (key === 'Enter') {
        // only blur on user pressing Enter, if scanner ends with /r, blur would be handled by useDebounce
        if (!isScannerInput(ts, timeStamp, timeBeforeScanTest)) {
          ref.current?.blur();
        }
        return;
      }

      if (key === 'Backspace' || key === 'Delete') {
        setScanValueMap([]);
        return;
      }

      // filter out special keyboard characters - `Enter`, `Shift`, etc.
      const char = key.length === 1 ? key : '';

      if (char) {
        setScanValueMap(s => [[timeStamp, char], ...s]);
      }
    };

    return (
      <input
        type="text"
        ref={ref}
        id="autoFocusInput"
        value={isEmpty ? '' : amount}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onClick={e => e.stopPropagation()}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        autoComplete="off"
        onFocus={autoFocus ? e => e.target.select() : undefined}
      />
    );
  },
);

export default ScanningRowQuantity;
